import * as React from 'react';
import { useEffect, useReducer, useState } from 'react';
import { channelValues } from 'shared/src/domain/line-item/line-item-channels-filters';
import { MgmtFeeLineItemApiCreateRequest } from 'shared/src/api/line-item-api-types';
import {
  MgmtFeeLineItemCalculatedFields,
  MgmtFeeLineItemMediaTeamCalculatedFields
} from '@/features/line-items/line-item-form/mgmt-fee/mgmt-fee-line-item-calculated-fields';
import { useUnsavedChangesWarning } from '@/features/line-items/line-item-form/ui/use-unsaved-changes-warning';
import { useScrollToFirstError } from '@/features/line-items/line-item-form/ui/use-scroll-to-first-error';
import { AdInfoSection } from '../ui/ad-info-section';
import { BillableRevenueBanner } from '../ui/billable-revenue-banner';
import { ButtonSelect } from '../ui/button-select';
import { DatesField } from '../ui/dates-field';
import { LockedChannelTacticType } from '../ui/locked-channel-tactic-type';
import { MediaTeamSection } from '../ui/media-team-section';
import { MoneyField } from '../ui/money-field';
import { NameSection } from '../ui/name-section';
import { NotesSection } from '../ui/notes-section';
import { Section } from '../ui/section';
import { TextField } from '../ui/text-field';
import {
  MgmtFeeFormState,
  mgmtFeeLineItemReducer
} from '@/features/line-items/line-item-form/common/form-reducer';
import {
  allocationSummary,
  isDirtyMgmtFee,
  selectAvailableTactics,
  selectAvailableUnitPriceTypes,
  selectChannelName,
  selectDisplayCPM,
  selectIsLocked,
  hasValidationErrors,
  selectMgmtFeeValidationErrors,
  selectPacingBlockRows,
  selectPriceAllocation,
  selectTacticName,
  selectTotalSpend,
  selectUnitPriceTypeName
} from '@/features/line-items/line-item-form/common/selectors';
import { Pacing } from '@/features/line-items/line-item-form/ui/pacing';
import { daysBetween } from 'shared/src/date-string-utils/date-utils';
import { formatMoney } from 'shared/src/utils/money/money-utils';
import { toMgmtFeeApiRequest } from '@/features/line-items/line-item-form/common/form-state';
import { unitShortLabel } from '@/features/line-items/line-item-form/utils/pacing-display';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';

type Props = {
  initialState: MgmtFeeFormState;
  onSubmit: (request: MgmtFeeLineItemApiCreateRequest) => void | Promise<void>;
  onCancel: () => void;
  typeSelector?: React.ReactNode;
  onFormChange?: (isDirty: boolean) => void;
};

export function MgmtFeeLineItemForm({
  initialState,
  onSubmit,
  onCancel,
  typeSelector,
  onFormChange
}: Props) {
  const { unitPriceTypes } = useChannelRatesContext();
  const [state, dispatch] = useReducer(mgmtFeeLineItemReducer, initialState);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isLocked = selectIsLocked(state);
  const errors = selectMgmtFeeValidationErrors(state);
  const hasErrors = hasValidationErrors(errors);
  const isDirty = isDirtyMgmtFee(state);
  useUnsavedChangesWarning(isDirty);
  useScrollToFirstError(errors, submitAttempted, key =>
    key === 'price' ? 'line-item-mgmt-fee-price' : undefined
  );
  useEffect(() => {
    onFormChange?.(isDirty);
  }, [isDirty, onFormChange]);
  const priceAllocation = selectPriceAllocation(state);
  const billableTotal = selectTotalSpend(state);
  const showBillable = isLocked && billableTotal > 0;
  const errorFor = (field: string): string | undefined => {
    if (!submitAttempted && !state.touched.has(field)) return undefined;
    return errors.fieldErrors[field];
  };

  const warningFor = (field: string): string | undefined => errors.fieldWarnings[field];

  const rootErrors = submitAttempted ? errors.formErrors : [];
  const rootWarnings = [
    ...Object.entries(errors.fieldWarnings)
      .filter(([key]) => !/^pacing_blocks\.\d+\.budget$/.test(key))
      .map(([, value]) => value),
    ...errors.formWarnings
  ];

  const channelOptions = toOptions(channelValues(state.channels));
  const tacticOptions = toOptions(selectAvailableTactics(state));
  const unitPriceTypeOptions = toOptions(selectAvailableUnitPriceTypes(state));

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitAttempted(true);
    if (hasErrors) return;
    const parsed = MgmtFeeLineItemApiCreateRequest.safeParse(toMgmtFeeApiRequest(state));
    if (!parsed.success) return;
    setIsSubmitting(true);
    try {
      await onSubmit(parsed.data);
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleCancel() {
    dispatch({ type: 'RESET', initialState });
    setSubmitAttempted(false);
    onCancel();
  }

  return (
    <form onSubmit={handleSubmit} className="@container flex flex-col p-6">
      <NameSection
        name={state.formData.name}
        mode={state.mode}
        isSubmitting={isSubmitting}
        submitDisabled={false}
        allocatedPrice={priceAllocation.allocated ?? state.proposalInfo.allocated_price}
        totalPrice={priceAllocation.total}
        plannedMargin={state.proposalInfo.planned_margin}
        targetMargin={state.proposalInfo.target_margin}
        rootErrors={rootErrors}
        rootWarnings={rootWarnings}
        hasErrors={hasErrors}
        submitAttempted={submitAttempted}
        nameError={errorFor('name')}
        onNameChange={value => dispatch({ type: 'SET_NAME', value })}
        onNameBlur={() => dispatch({ type: 'BLUR_FIELD', field: 'name' })}
        onCancel={handleCancel}
        typeSelector={typeSelector}
      />

      <Section heading="Pricing">
        <div className="flex gap-4">
          <div className="flex-1">
            <MoneyField
              id="line-item-mgmt-fee-price"
              label="Management Fee"
              description="This will appear in-table as Price"
              value={state.formData.price}
              decimalScale={2}
              onChange={value => dispatch({ type: 'SET_PRICE', value })}
              onBlur={() => dispatch({ type: 'BLUR_FIELD', field: 'price' })}
              error={errorFor('price')}
            />
          </div>
          <div className="flex-1">
            <MoneyField
              id="line-item-media-budget"
              label="Media Budget"
              description="Set a budget for media spend"
              value={state.formData.media_budget}
              decimalScale={2}
              onChange={value => dispatch({ type: 'SET_MEDIA_BUDGET', value })}
              onBlur={() => dispatch({ type: 'BLUR_FIELD', field: 'media_budget' })}
              error={errorFor('media_budget')}
            />
            {showBillable ? <BillableRevenueBanner total={billableTotal} /> : null}
          </div>
        </div>
        <TextField
          id="line-item-purchase-order-number"
          label="PO Number (Optional)"
          value={state.formData.purchase_order_number}
          onChange={value =>
            dispatch({
              type: 'SET_PO_NUMBER',
              value: value === '' ? undefined : value
            })
          }
          onBlur={() => dispatch({ type: 'BLUR_FIELD', field: 'purchase_order_number' })}
          error={errorFor('purchase_order_number')}
        />
        {isLocked ? (
          <LockedChannelTacticType
            channelName={selectChannelName(state)}
            tacticName={selectTacticName(state)}
            unitPriceTypeName={selectUnitPriceTypeName(state)}
          />
        ) : (
          <>
            <ButtonSelect<number>
              id="line-item-channel"
              label="Channel"
              description="Select one"
              value={state.formData.channel_id}
              options={channelOptions}
              onChange={channel_id =>
                channel_id !== undefined && dispatch({ type: 'CHANGE_CHANNEL', channel_id })
              }
              error={errorFor('channel_id')}
            />
            <ButtonSelect<number>
              id="line-item-tactic"
              label="Tactic"
              description="Select one"
              placeholder="Select Channel for options"
              value={state.formData.tactic_id}
              options={tacticOptions}
              onChange={tactic_id =>
                tactic_id !== undefined && dispatch({ type: 'CHANGE_TACTIC', tactic_id })
              }
              disabled={state.formData.channel_id == null || tacticOptions.length === 0}
              error={errorFor('tactic_id')}
            />
            <ButtonSelect<number>
              id="line-item-unit-price-type"
              label="Unit Price Type"
              description="Select one"
              placeholder="Select Tactic for options"
              value={state.formData.unit_price_type_id}
              options={unitPriceTypeOptions}
              onChange={unit_price_type_id =>
                unit_price_type_id !== undefined &&
                dispatch({ type: 'CHANGE_UNIT_PRICE_TYPE', unit_price_type_id })
              }
              disabled={state.formData.tactic_id == null || unitPriceTypeOptions.length === 0}
              error={errorFor('unit_price_type_id')}
            />
          </>
        )}
        <MgmtFeeLineItemCalculatedFields inputs={toCalculatedFieldsInput(state)} />
      </Section>

      <Section heading="Dates">
        <DatesField
          id="line-item-dates"
          value={state.formData.dates}
          minDate={state.proposalInfo.campaign_dates.from}
          maxDate={state.proposalInfo.campaign_dates.to}
          onChange={value => dispatch({ type: 'SET_DATES', value })}
          error={errorFor('dates')}
          warning={warningFor('dates')}
        />
      </Section>

      <Pacing
        config={{
          lineItemType: 'management_fee',
          rows: selectPacingBlockRows(state),
          spend: state.dailyBudgetSpent,
          allocation: allocationSummary(state, state.formData.media_budget),
          monthly_rollover: state.formData.monthly_rollover,
          pacing_amount: state.formData.media_budget,
          unit_cost: state.formData.planned_unit_cost,
          dates: state.formData.dates,
          pacing_type: state.formData.pacing_type,
          label: 'Budget',
          unit_label: unitShortLabel(state.formData.unit_price_type_id, unitPriceTypes),
          is_published: isLocked
        }}
        dispatch={dispatch}
        pacingDescription={<PacingDescription state={state} />}
        errorFor={errorFor}
        warningFor={warningFor}
      />

      <AdInfoSection state={state} dispatch={dispatch} errorFor={errorFor} />

      <NotesSection state={state} dispatch={dispatch} errorFor={errorFor} />

      <MediaTeamSection
        state={state}
        dispatch={dispatch}
        errorFor={errorFor}
        calculatedFields={
          <MgmtFeeLineItemMediaTeamCalculatedFields inputs={toCalculatedFieldsInput(state)} />
        }
      />
    </form>
  );
}

function PacingDescription({ state }: { state: MgmtFeeFormState }) {
  const { media_budget, dates } = state.formData;
  const dayCount = daysBetween(dates.from, dates.to);
  const formattedPrice = media_budget === undefined ? '—' : formatMoney(media_budget);
  return (
    <div className="text-sm text-gray-500">
      Define how you want to distribute spend of the line item's{' '}
      <span className="font-semibold">{formattedPrice} Media Budget </span> across its{' '}
      <span className="font-semibold">{dayCount}</span> day schedule
    </div>
  );
}

function toCalculatedFieldsInput(state: MgmtFeeFormState) {
  const { price, media_budget, unit_price_type_id, planned_unit_cost } = state.formData;
  return {
    price,
    media_budget,
    unit_price_type_id: unit_price_type_id ?? undefined,
    planned_unit_cost: selectDisplayCPM(state, planned_unit_cost),
    agency_markup_rate: state.proposalInfo.agency_markup_rate,
    referral_commission_rate: state.proposalInfo.referral_commission_rate
  };
}

function toOptions<V>(items: readonly { id: V; name: string }[]): { value: V; label: string }[] {
  return items.map(item => ({ value: item.id, label: item.name }));
}
